import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { isUUID } from 'validator';

export const getQueryKey = (gameId: string, orgId?: number) => {
  return ['Games', gameId, 'ClipGroupCounts', orgId];
};

interface ClipGroupCountsResponse {
  success: boolean;
  clipGroupCounts: {
    custom: Record<string, number>;
    ses: Record<string, number>;
  };
  totalClipGroups: number;
}

export const useClipGroupCountsQuery = (gameIdParam?: string) => {
  const currentGameId = useCurrentGameId();
  const gameId = gameIdParam || currentGameId;
  const { currentOrg } = useUserStateContext();
  const orgId = (currentOrg as any)?.id;

  const isValidGame = !!gameId && isUUID(gameId);
  const queryKey = getQueryKey(gameId, orgId);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!isValidGame) {
        return null;
      }
      const params = new URLSearchParams();
      if (orgId) {
        params.append('orgId', String(orgId));
      }
      const response = await get<ClipGroupCountsResponse>(
        `/v1.0/games/${gameId}/clip-group-counts${params.toString() ? '?' + params.toString() : ''}`
      );
      return response.data;
    },
    enabled: isValidGame,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    clipGroupCounts: query.data?.clipGroupCounts || { custom: {}, ses: {} },
    totalClipGroups: query.data?.totalClipGroups || 0,
  };
};

export default useClipGroupCountsQuery;
